import { LocalDate } from '../../../utilities/local-date';
import type { EmployeeForStaffingCalendar } from './employee-for-staffing-calendar';
import type { ScheduleDayData } from './schedule-day-data';
import { getCurrentSchedule } from './get-current-schedule';
import type { WorkScheduleDay } from '@trinnvis/dabih-api-client';
import { WorkScheduleRestPeriodEnum } from '@trinnvis/dabih-api-client';
import { getScheduleForDay } from './get-schedule-for-day';
import { durationInMinutes } from './duration-in-minutes';
import { _holidaysForStaffingCalendar } from './_holidays-for-staffing-calendar';
import { getScheduleException } from './get-schedule-exception';

const EMPTY_SCHEDULE_DAY_DATA: ScheduleDayData = {
  workHours: 0,
  start: '',
  end: '',
  holidays: false,
  exception: false,
  exceptionWork: false,
  scheduleStart: '',
  scheduleEnd: '',
};

/**
 * Takes a day date, an employee and a list of holidays
 * Returns the number of work hours and work start and end time
 */
export function getScheduleDayData(day: LocalDate, employee: EmployeeForStaffingCalendar): ScheduleDayData {
  // Days outside employment period are not work days
  if (employee.employmentStartDate && day.isBefore(LocalDate.fromString(employee.employmentStartDate))) {
    return { ...EMPTY_SCHEDULE_DAY_DATA };
  }
  if (employee.employmentEndDate && day.isAfter(LocalDate.fromString(employee.employmentEndDate))) {
    return { ...EMPTY_SCHEDULE_DAY_DATA };
  }

  const scheduleDayData: ScheduleDayData = {
    workHours: 0,
    start: '',
    end: '',
    holidays: true,
    exception: false,
    exceptionWork: false,
    scheduleStart: '',
    scheduleEnd: '',
  };
  let workMinutes: number;
  let breakMinutes = 0;

  // FIND CURRENT SCHEDULE
  const currentSchedule = getCurrentSchedule(day, employee);

  scheduleDayData.holidays = currentSchedule.holidaysAreTimeOff;
  if (currentSchedule.restPeriod === WorkScheduleRestPeriodEnum.WithoutPay) {
    breakMinutes = 30;
  }
  // FIND CURRENT DAY IN SCHEDULE
  const weeksInSchedule = currentSchedule.workWeeks.length;
  const scheduleDay: WorkScheduleDay = getScheduleForDay(weeksInSchedule, currentSchedule, day);
  if (scheduleDay === undefined) {
    console.log('sss');
  }
  if (!scheduleDay.workDay) {
    workMinutes = 0;
  } else {
    if (
      !currentSchedule.workHoursDefinedPerDay &&
      currentSchedule.workDayStartTime !== undefined &&
      currentSchedule.workDayEndTime !== undefined
    ) {
      scheduleDayData.start = currentSchedule.workDayStartTime;
      scheduleDayData.scheduleStart = currentSchedule.workDayStartTime;
      scheduleDayData.end = currentSchedule.workDayEndTime;
      scheduleDayData.scheduleEnd = currentSchedule.workDayEndTime;
      workMinutes = durationInMinutes(currentSchedule.workDayStartTime, currentSchedule.workDayEndTime);
    } else if (scheduleDay.start && scheduleDay.end) {
      scheduleDayData.start = scheduleDay.start;
      scheduleDayData.scheduleStart = scheduleDay.start;
      scheduleDayData.end = scheduleDay.end;
      scheduleDayData.scheduleEnd = scheduleDay.end;
      workMinutes = durationInMinutes(scheduleDay.start, scheduleDay.end);
    } else {
      throw new Error('Illegal state (E422)');
    }
  }
  if (workMinutes < 30) {
    breakMinutes = 0;
  }
  scheduleDayData.workHours = (workMinutes - breakMinutes) / 60;
  if (scheduleDayData.holidays && day.toString() in _holidaysForStaffingCalendar) {
    scheduleDayData.workHours = 0;
    scheduleDayData.start = '';
    scheduleDayData.scheduleStart = '';
    scheduleDayData.end = '';
    scheduleDayData.scheduleEnd = '';
  }

  // CHECK FOR SCHEDULE EXCEPTION
  const exception = getScheduleException(day, employee);
  if (exception !== undefined && exception.start && exception.end) {
    scheduleDayData.exception = true;
    // Set start and end for exception
    scheduleDayData.start = exception.start;
    scheduleDayData.end = exception.end;
    if (exception.work) {
      scheduleDayData.exceptionWork = true;
      // Get minutes for exception
      workMinutes = durationInMinutes(exception.start, exception.end);
      scheduleDayData.workHours = (workMinutes - breakMinutes) / 60;
    } else {
      scheduleDayData.workHours = 0;
    }
  }
  return scheduleDayData;
}
